import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Container,
  Button,
  Alert,
  Stack,
  Link,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Helmet } from 'react-helmet-async';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LoginIcon from '@mui/icons-material/Login';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { SITE_NAME } from '../constants/site';
import { createGuestAccount } from '../services/api';
import { isPwa } from '../utils/isPwa';
import Dialog from '../components/dialogs/Dialog';

const GuestStartPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // アプリとブラウザはデータの保存領域が別（iOSでは特に）。ゲストを開始してから
  // アプリを追加してもデータは引き継がれないため、開始前のこの画面でのみ案内する
  const recommendPwaInstall = isMobile && !isPwa();

  const handleStart = async () => {
    setLoading(true);
    try {
      const data = await createGuestAccount();

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // ヘッダー等のログイン状態を更新
      window.dispatchEvent(new CustomEvent('loginSuccess', { detail: data.user }));

      navigate('/', { replace: true });
    } catch (e: any) {
      if (e.response?.status === 429) {
        setError('ゲスト開始の回数制限に達しました。しばらく時間をおいてからお試しください。');
      } else {
        setError('ゲストの開始に失敗しました。時間をおいて再度お試しください。');
      }
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ゲストとして始める | {SITE_NAME}</title>
      </Helmet>

      <Box
        sx={{
          background: (theme) => `linear-gradient(145deg, ${theme.palette.background.paper}, rgba(0, 0, 0, 0.2))`,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              background: (theme) => `rgba(${theme.palette.mode === 'dark' ? '255,255,255,0.03' : '0,0,0,0.02'})`,
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              ゲストモードについて
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              登録なしでゲームライブラリ（ゲームの登録・プレイ状況の記録）をお試しいただけます。
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              すでにアカウントをお持ちの方は、必ずログインしてご利用ください。
              ゲストで記録したデータは、後から既存アカウントにログインしても<strong>引き継がれません</strong>。
              <Box sx={{ mt: 1 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/login')}
                  sx={{ fontWeight: 600 }}
                >
                  ログイン画面へ →
                </Link>
              </Box>
            </Alert>

            <Box
              sx={{
                p: { xs: 1.75, sm: 2 },
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'warning.main',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 167, 38, 0.08)'
                    : 'rgba(237, 108, 2, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                <ReportProblemOutlinedIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  データが失われないためにご確認ください
                </Typography>
              </Box>

              <Stack spacing={1.25}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0, mt: '2px' }} />
                  <Typography variant="body2" color="text.secondary">
                    ゲストデータの保持保証期間は<strong>30日間</strong>です。それ以降のデータは定期的に削除されます。
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CloudOffIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0, mt: '2px' }} />
                  <Typography variant="body2" color="text.secondary">
                    ゲストの識別データはご利用の環境のみに紐づきます。<strong>ブラウザデータの消去など環境側の操作で認証情報が失われる</strong>おそれがあるため、続けてご利用になる場合は<strong>早めのアカウント作成を強く推奨します</strong>。
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box component="ul" sx={{ pl: 2.5, my: 2, color: 'text.secondary', '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body2">
                  ゲストで利用できるのはゲームライブラリの基本的な管理機能のみです。
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  アカウント登録（無料）はいつでも可能で、
                  <Box
                    component="strong"
                    sx={{ color: 'primary.main', fontWeight: 700 }}
                  >
                    記録したデータをそのまま引き継ぐことができます
                  </Box>
                  。
                </Typography>
              </li>
            </Box>

            {recommendPwaInstall && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.25,
                  mb: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                }}
              >
                <InstallMobileIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0, mt: '2px' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    先にアプリを追加してから始めるのがおすすめです
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    本登録前の場合、ブラウザで始めた後にアプリを追加しても
                    <strong>ゲストのデータは引き継がれません</strong>。
                    また、アプリの方が快適にご利用いただけるため、アプリで体験することを強く推奨します。
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/install-pwa')}
                    sx={{ fontWeight: 600, fontSize: '0.75rem', mt: 0.25 }}
                  >
                    インストール方法を見る →
                  </Link>
                </Box>
              </Box>
            )}

            <Stack spacing={1.5}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleStart}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PersonOutlineIcon />}
              >
                {loading ? '開始しています…' : 'ゲストとして始める'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<LoginIcon />}
                onClick={() => navigate('/login')}
              >
                ログイン・アカウント登録
              </Button>
              <Button color="inherit" onClick={() => navigate(-1)}>
                戻る
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Dialog
        open={!!error}
        onClose={() => setError(null)}
        title="エラー"
        content={error || ''}
      />
    </>
  );
};

export default GuestStartPage;
